import * as openai from './openai';
import * as anthropic from './anthropic';

// Model storage keys by provider
const MODEL_STORAGE_KEYS = {
  openai: 'paybyrd_openai_model',
  anthropic: 'paybyrd_anthropic_model',
};

// Default models by provider
const DEFAULT_MODELS = {
  openai: 'gpt-4-turbo',
  anthropic: 'claude-4-sonnet-20250514',
};

/**
 * Unified Chat Service to handle different AI providers
 */
export default class ChatService {
  constructor() {
    this.provider = localStorage.getItem('paybyrd_ai_provider') || 'openai';
    // Ensure API key is properly loaded from localStorage
    this._hasApiKey = this._checkApiKeyInStorage();
  }
  
  /**
   * Private method to check API key directly in localStorage
   * @returns {boolean} True if API key exists in storage
   */
  _checkApiKeyInStorage() {
    const keyStorageKey = this.provider === 'openai' 
      ? 'paybyrd_openai_api_key' 
      : 'paybyrd_anthropic_api_key';
    return !!localStorage.getItem(keyStorageKey);
  }

  /**
   * Get the selected model for the current provider
   * @returns {string} Model name
   */
  getModel() {
    const storageKey = MODEL_STORAGE_KEYS[this.provider];
    return localStorage.getItem(storageKey) || DEFAULT_MODELS[this.provider];
  }

  /**
   * Set the model for the current provider
   * @param {string} model - Model name
   */
  setModel(model) {
    const storageKey = MODEL_STORAGE_KEYS[this.provider];
    localStorage.setItem(storageKey, model);
  }

  /**
   * Get current provider
   * @returns {string} Current provider name
   */
  getProvider() {
    return this.provider;
  }

  /**
   * Set AI provider
   * @param {string} provider - Provider name ('openai' or 'anthropic')
   */
  setProvider(provider) {
    if (provider !== 'openai' && provider !== 'anthropic') {
      throw new Error('Invalid provider');
    }

    this.provider = provider;
    localStorage.setItem('paybyrd_ai_provider', provider);
    
    // Update API key status for the new provider
    this._hasApiKey = this._checkApiKeyInStorage();
  }

  /**
   * Check if API key is set for the current provider
   * @returns {boolean} True if API key is set
   */
  hasApiKey() {
    // First check our cached value
    if (this._hasApiKey !== undefined) {
      return this._hasApiKey;
    }
    
    // Then check directly from localStorage
    this._hasApiKey = this.provider === 'openai'
      ? openai.hasOpenAIApiKey()
      : anthropic.hasAnthropicApiKey();
      
    return this._hasApiKey;
  }

  /**
   * Set API key for the current provider
   * @param {string} apiKey - API key
   */
  setApiKey(apiKey) {
    if (this.provider === 'openai') {
      openai.setOpenAIApiKey(apiKey);
    } else {
      anthropic.setAnthropicApiKey(apiKey);
    }
    // Update the cached API key status
    this._hasApiKey = true;
  }

  /**
   * Clear API key for the current provider
   */
  clearApiKey() {
    if (this.provider === 'openai') {
      openai.clearOpenAIApiKey();
    } else {
      anthropic.clearAnthropicApiKey();
    }
    // Update the cached API key status
    this._hasApiKey = false;
  }

  /**
   * Get chat history for the current provider and store
   * @param {Object} store - The selected store (optional)
   * @returns {Array} Chat history
   */
  getChatHistory(store = null) {
    const storeId = store?.value || 'global';
    if (this.provider === 'openai') {
      return openai.getChatHistory(storeId);
    } else {
      return anthropic.getChatHistory(storeId);
    }
  }

  /**
   * Save chat history for the current provider and store
   * @param {Array} history - Chat history to save
   * @param {Object} store - The selected store (optional)
   */
  saveChatHistory(history, store = null) {
    const storeId = store?.value || 'global';
    if (this.provider === 'openai') {
      openai.saveChatHistory(history, storeId);
    } else {
      anthropic.saveChatHistory(history, storeId);
    }
  }

  /**
   * Clear chat history for the current provider and store
   * @param {Object} store - The selected store (optional)
   */
  clearChatHistory(store = null) {
    const storeId = store?.value || 'global';
    if (this.provider === 'openai') {
      openai.clearChatHistory(storeId);
    } else {
      anthropic.clearChatHistory(storeId);
    }
  }

  /**
   * Send a message to the AI service with the current provider
   * @param {string} message - User message
   * @param {Array} previousMessages - Previous messages in the conversation
   * @param {string} accessToken - Auth token for API authorization
   * @param {Object} selectedStore - Selected store for context
   * @returns {Promise<string>} AI response
   */
  async sendMessage(
    message,
    previousMessages = [],
    accessToken = null,
    selectedStore = null
  ) {
    if (!this.hasApiKey()) {
      throw new Error('ai_chat_api_key_missing');
    }

    try {
      if (this.provider === 'openai') {
        return await openai.sendMessageToOpenAI(
          message,
          previousMessages,
          accessToken,
          selectedStore
        );
      } else {
        return await anthropic.sendMessageToAnthropic(
          message,
          previousMessages,
          accessToken,
          selectedStore
        );
      }
    } catch (error) {
      console.error(
        `Error in ChatService.sendMessage with ${this.provider}:`,
        error
      );
      throw error;
    }
  }
}
